import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { IEventSummary, IShow, IShowElement } from "@eagerdog/interfaces";

import TabContent, { ITab, useTabContent } from "src/components/TabContent/TabContent";

import NotificationsTab from "./NotificationsTab/NotificationsTab";
import JudgeSchedulesTab from "./JudgeSchedulesTab/JudgeSchedulesTab";
import RunOrderTab from "./RunOrderTab/RunOrderTab";
import ExerciseTab from "./ExerciseTab/ExerciseTab";
import WaitlistTab from "./WaitlistTab/WaitlistTab";
import ExercisesTab from "./ExercisesTab/ExercisesTab";
// import ScentworkElementsTab from "./ScentworkElementsTab/ScentworkElementsTab";
import AdditionalSettingsTab from "./AdditionalSettingsTab/AdditionalSettingsTab";
import { ScentLocationsTab } from "./ScentLocationsTab";
import { AKCAgilitySCTTab } from "./AKCAgilitySCTTab";
import BreedsTab from "./BreedsTab/BreedsTab";
import ShowSettingsTab from "./ShowSettingsTab/ShowSettingsTab";
import DayOfShowTab from "./DayOfShowTab/DayOfShowTab";
import WeightSetupTab from "./WeightSetupTab/WeightSetupTab";

import styles from "./ManageShow.module.scss";

/**
 * Returns true if the given sanctioning_club + show_type combination
 * has a Judge Schedules tab in ManageShow. Used to gate the read-only
 * judge step in ShowForm so that show types without a Judge Schedules
 * tab can still edit judges through the wizard.
 */
export function showTypeSupportsJudgeSchedules(sanctioningClub: string, showType: string): boolean {
  const AKCSupportedShows = [
    Constants.show_type.obedience,
    Constants.show_type.rally_obedience,
    Constants.show_type.scentwork,
    Constants.show_type.conformation,
    Constants.show_type.agility,
    Constants.show_type.fetch,
    Constants.show_type.farm_dog,
    Constants.show_type.herding,
    Constants.show_type.pre_trial_herding,
    Constants.show_type.instinct_test_herding,
  ];
  const UKCSupportedShows = [
    Constants.show_type.nosework,
    Constants.show_type.obedience,
    Constants.show_type.rally_obedience,
    Constants.show_type.conformation,
    Constants.show_type.shed_dog,
    Constants.show_type.precision_coursing,
  ];
  const PWDCASupportedShows = [Constants.show_type.water_dog];
  const ABISupportedShows = [Constants.show_type.country_rat_race, Constants.show_type.urban_rat_race, Constants.show_type.scent_dog_games, Constants.show_type.lost_and_found];
  const GeneralSupportedShows = [Constants.show_type.sniff_n_go, Constants.show_type.dock_diving, Constants.show_type.obedience_show_n_go, Constants.show_type.general_rally];
  const CWagsSupportedShows = [
    Constants.show_type.scent,
    Constants.show_type.obedience,
    Constants.show_type.rally_obedience,
    Constants.show_type.cwags_games,
  ];
  const BHASupportedShows = [Constants.show_type.barn_hunt];
  const ASFASupportedShows = [Constants.show_type.field_trial];
  const FHSupportedShows = [
    Constants.show_type.rally_obedience,
    Constants.show_type.agility,
  ];
  const AHBASupportedShows = [Constants.show_type.herding];

  return (
    (sanctioningClub === Constants.sanctioning_club.AKC && AKCSupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.UKC && UKCSupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.PWDCA && PWDCASupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.ABI && ABISupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.GENERAL && GeneralSupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.C_WAGS && CWagsSupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.BHA && BHASupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.ASFA && ASFASupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.FH && FHSupportedShows.includes(showType)) ||
    (sanctioningClub === Constants.sanctioning_club.AHBA && AHBASupportedShows.includes(showType))
  );
}

interface IProps {
  event: IEventSummary,
  show: IShow,
  onUpdate(): void,
  level?: string,
  initialTab?: string // e.g., "Waitlist", "Notifications", "Judge Schedules", etc.
}

const ManageShow: React.FC<IProps> = React.memo((props) => {
  const { show: propsShow, onUpdate } = props;
  const [loaded, setLoaded] = useState<boolean>(false);
  const [show, setShow] = useState<IShow>(propsShow);
  const { activeTab, setTab } = useTabContent();
  
  // State for tracking pending judge schedule changes
  const [, setJudgeScheduleChangesPending] = useState<boolean>(false);
  
  // State for dynamic show title with timing information
  const [showTitleData, setShowTitleData] = useState<{ mainTitle: string; subTitle?: string }>({
    mainTitle: `${propsShow.show_type}${propsShow.is_fun_match ? " (Fun Match)" : ""} ${propsShow.custom_show_name || propsShow.show_name} (${moment(propsShow.show_date).format("MMM Do")})`
  });
  
  // Refs for communication between tabs
  const runOrderSyncRef = useRef<(() => void) | null>(null);
  const runOrderSetPendingRef = useRef<((pending: boolean) => void) | null>(null);
  const judgeScheduleReloadRef = useRef<(() => void) | null>(null);

  // Update local show state when props change
  useEffect(() => {
    setShow(propsShow);
  }, [propsShow]);

  // Callback for child components to update show data
  const handleShowUpdate = useCallback((updatedShow: IShow) => {

    setShow(updatedShow);
    onUpdate(); // Also notify parent
  }, [onUpdate]);

  // Callback for Judge Schedule to notify of pending changes
  const handleJudgeScheduleChange = useCallback(() => {
    setJudgeScheduleChangesPending(true);
    if (runOrderSetPendingRef.current) {
      runOrderSetPendingRef.current(true);
    }
    // Auto-reload RunOrderTab so judge name assignments stay current after drags
    if (runOrderSyncRef.current) {
      runOrderSyncRef.current();
    }
  }, []);

  // Callback for Run Order to manually sync
  const handleManualSync = useCallback(() => {
    setJudgeScheduleChangesPending(false);
    if (runOrderSyncRef.current) {
      runOrderSyncRef.current();
    }
    if (runOrderSetPendingRef.current) {
      runOrderSetPendingRef.current(false);
    }
  }, []);

  // Callback for Judge Schedule to update show title with timing
  const handleShowTitleChange = useCallback((titleData: { mainTitle: string; subTitle?: string }) => {
    setShowTitleData(titleData);
  }, []);

  const supportsJudgeSchedules = useCallback(() => {
    return showTypeSupportsJudgeSchedules(props.event.sanctioning_club, props.show.show_type);
  }, [props.event.sanctioning_club, props.show.show_type]);

  const supportsRunOrder = useCallback(() => {
    // Most Judge Schedule shows support RunOrder, except UKC Conformation
    if (props.event.sanctioning_club === Constants.sanctioning_club.UKC && 
        props.show.show_type === Constants.show_type.conformation) {
      return false; // Disable RunOrder for UKC Conformation for now
    }
    
    // Fast CAT supports Run Order but not Judge Schedules
    if (props.event.sanctioning_club === Constants.sanctioning_club.AKC && 
        props.show.show_type === Constants.show_type.fast_cat) {
      return true;
    }
    
    // UKC Agility supports Run Order but not Judge Schedules
    if (props.event.sanctioning_club === Constants.sanctioning_club.UKC && 
        props.show.show_type === Constants.show_type.agility) {
      return true;
    }
    
    return supportsJudgeSchedules();
  }, [props.event.sanctioning_club, props.show.show_type, supportsJudgeSchedules]);

  const showHasExercises = useCallback(() => {
    for (let se in props.show.show_elements) {
      let showElement:IShowElement = props.show.show_elements[se];

      if (showElement.exercises !== undefined && showElement.exercises.length > 0) {
        return true;
      }
    }

    return false;
  }, [props.show.show_elements]);

  const showExercisesTab = useCallback(() => {
    return (
      props.show.show_type === Constants.show_type.obedience &&
      props.event.sanctioning_club === Constants.sanctioning_club.AKC
    );
  }, [props.show.show_type, props.event.sanctioning_club]);

  // const showScentworkElementsTab = useCallback(() => {
  //   return (
  //     props.show.show_type === Constants.show_type.scentwork &&
  //     props.event.sanctioning_club === Constants.sanctioning_club.AKC
  //   );
  // }, [props.show.show_type, props.event.sanctioning_club]);

  const showAgilitySettingsTab = useCallback(() => {
    return (
      props.show.show_type === Constants.show_type.agility &&
      props.event.sanctioning_club === Constants.sanctioning_club.UKC
    );
  }, [props.show.show_type, props.event.sanctioning_club]);

  const showAKCAgilitySettingsTab = useCallback(() => {
    return (
      props.show.show_type === Constants.show_type.agility &&
      props.event.sanctioning_club === Constants.sanctioning_club.AKC
    );
  }, [props.show.show_type, props.event.sanctioning_club]);

  const showScentSettingsTab = useCallback(() => {
    return (
      props.show.show_type === Constants.show_type.scent &&
      props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS
    );
  }, [props.show.show_type, props.event.sanctioning_club]);

  const showShowSettingsTab = useCallback(() => {
    return (
      (props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
      (props.show.show_type === Constants.show_type.rally_obedience ||
        props.show.show_type === Constants.show_type.obedience ||
        props.show.show_type === Constants.show_type.scentwork ||
        props.show.show_type === Constants.show_type.conformation)) ||
      (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
        props.show.show_type === Constants.show_type.conformation)
    );
  }, [props.event.sanctioning_club, props.show.show_type]);

  const showDayOfShowTab = useCallback(() => {
    return (
      props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
      props.show.show_type === Constants.show_type.conformation
    );
  }, [props.event.sanctioning_club, props.show.show_type]);

  const showWeightSetupTab = useCallback(() => {
    return props.show.show_type === Constants.show_type.weight_pull;
  }, [props.show.show_type]);

  // Memoize these values to prevent component remounting
  const shouldShowJudgeSchedules = useMemo(() => supportsJudgeSchedules(), [supportsJudgeSchedules]);
  const shouldShowRunOrder = useMemo(() => supportsRunOrder(), [supportsRunOrder]);
  const shouldShowExercisesTab = useMemo(() => showExercisesTab(), [showExercisesTab]);
  const shouldShowAgilitySettingsTab = useMemo(() => showAgilitySettingsTab(), [showAgilitySettingsTab]);
  const shouldShowAKCAgilitySettingsTab = useMemo(() => showAKCAgilitySettingsTab(), [showAKCAgilitySettingsTab]);
  const shouldShowScentSettingsTab = useMemo(() => showScentSettingsTab(), [showScentSettingsTab]);
  const shouldShowShowSettingsTab = useMemo(() => showShowSettingsTab(), [showShowSettingsTab]);
  const shouldShowDayOfShowTab = useMemo(() => showDayOfShowTab(), [showDayOfShowTab]);
  const shouldShowWeightSetupTab = useMemo(() => showWeightSetupTab(), [showWeightSetupTab]);
  const hasExercises = useMemo(() => showHasExercises(), [showHasExercises]);

  // Memoize showElements to prevent WaitlistTab remounting
  const memoizedShowElements = useMemo(() => {
    return props.show.show_elements.map((s: IShowElement, index: number) => {
      return {
        label: s.level ? s.level : s.show_element,
        checked: true
      };
    });
  }, [props.show.show_elements]);

  const [tabs, setTabs]= useState<ITab[]>();

  useEffect(() => {
    if (!loaded) {
      let _tabs:ITab[] = [
        { name: "Notifications", actions: [] },
        { name: "Waitlist", actions: [] },
        { name: "Breeds", actions: [] },
      ];
      if (shouldShowJudgeSchedules) {
        _tabs.push({ name: "Judge Schedules", actions: [] });
      }
      if (shouldShowRunOrder) {
        _tabs.push({ name: "Run Order", actions: [] });
      }
      if (shouldShowExercisesTab) {
        _tabs.push({ name: "Course Setup", actions: [] });
      } else if (hasExercises) {
        _tabs.push({ name: "Course Setup", actions: [] });
      }
      if (shouldShowAgilitySettingsTab) {
        // Use "ACT Settings" for UKC Agility, "Additional Settings" for others
        const tabName = props.show.show_type === Constants.show_type.agility && 
                        props.event.sanctioning_club === Constants.sanctioning_club.UKC 
                        ? "SCT Settings" 
                        : "Additional Settings";
        _tabs.push({ name: tabName, actions: [] });
      }
      if (shouldShowAKCAgilitySettingsTab) {
        _tabs.push({ name: "SCT Settings", actions: [] });
      }
      if (shouldShowScentSettingsTab) {
        _tabs.push({ name: "Scents Settings", actions: [] });
      }
      if (shouldShowShowSettingsTab) {
        _tabs.push({ name: "Settings", actions: [] });
      }
      if (shouldShowDayOfShowTab) {
        _tabs.push({ name: "Day of Show", actions: [] });
      }
      if (shouldShowWeightSetupTab) {
        _tabs.push({ name: "Weight Setup", actions: [] });
      }
      setTabs(_tabs);
      
      // Set initial tab if specified
      if (props.initialTab) {
        const tabIndex = _tabs.findIndex((t: ITab) => t.name === props.initialTab);
        if (tabIndex !== -1) {
          setTab(tabIndex);
        }
      } else if (props.level) {
        setTab(_tabs.findIndex((t: ITab) => t.name === "Course Setup"));
      }
      setLoaded(true);
    }
    }, [loaded, hasExercises, shouldShowJudgeSchedules, shouldShowRunOrder, props.level, props.initialTab, setTab, shouldShowExercisesTab, shouldShowAgilitySettingsTab, shouldShowShowSettingsTab, shouldShowDayOfShowTab, shouldShowWeightSetupTab]);

  return (
    <div className={styles.ManageShow}>
      <div className={styles.title}>
        <span className={styles.ModalTitle}>Manage Show</span>
        <span className={styles.ShowName}>{showTitleData.mainTitle}</span>
        {showTitleData.subTitle && <span className={styles.ShowSubTitle}>{showTitleData.subTitle}</span>}
      </div>
      {loaded && tabs !== undefined && <div className={styles.manageShowContent}>
        <TabContent className={"ManageShowTabs"} setTab={setTab} activeTab={activeTab} tabs={tabs}>
          <NotificationsTab key="notifications" event={props.event} show={show} onUpdate={props.onUpdate} />
          <WaitlistTab
            key="waitlist"
            event={props.event}
            showType={show.show_type}
            show={show}
            showElements={memoizedShowElements}
            onUpdate={props.onUpdate}
          />
          <BreedsTab key="breeds" event={props.event} show={show} onUpdate={props.onUpdate} />
          {shouldShowJudgeSchedules && <JudgeSchedulesTab key="judge-schedules" event={props.event} show={show} onUpdate={handleShowUpdate} onJudgeScheduleChange={handleJudgeScheduleChange} onShowTitleChange={handleShowTitleChange} onRegisterReload={(fn) => { judgeScheduleReloadRef.current = fn; }} />}
          {shouldShowRunOrder && <RunOrderTab 
            key="run-order" 
            event={props.event} 
            show={show} 
            onUpdate={handleShowUpdate} 
            onRegisterSync={(syncFn) => { runOrderSyncRef.current = syncFn; }}
            onRegisterSetPending={(setPendingFn) => { runOrderSetPendingRef.current = setPendingFn; }}
            onManualSync={handleManualSync}
            onRunOrderSave={() => { judgeScheduleReloadRef.current?.(); }}
          />}
          {shouldShowExercisesTab ? (
            <ExercisesTab key="exercises" event={props.event} show={props.show} onUpdate={props.onUpdate} />
          ) : (
            hasExercises && <ExerciseTab key="exercise" startAt={props.level ? props.level : undefined} event={props.event} show={props.show} onUpdate={props.onUpdate} />
          )}
          {/*TODO: remove*/}
          {/*{shouldShowScentworkElementsTab && <ScentworkElementsTab key="scentwork-elements" event={props.event} show={props.show} onUpdate={props.onUpdate} />}*/}
          {shouldShowAgilitySettingsTab && <AdditionalSettingsTab key="additional-settings" event={props.event} show={props.show} />}
          {shouldShowAKCAgilitySettingsTab && <AKCAgilitySCTTab key="akc-agility-sct" event={props.event} show={props.show} />}
          {shouldShowScentSettingsTab && <ScentLocationsTab key="scents-settings" event={props.event} show={props.show} />}
          {shouldShowShowSettingsTab && <ShowSettingsTab key="show-settings" event={props.event} show={show} onUpdate={props.onUpdate} />}
          {shouldShowDayOfShowTab && <DayOfShowTab key="day-of-show" event={props.event} show={show} onUpdate={props.onUpdate} />}
          {shouldShowWeightSetupTab && <WeightSetupTab key="weight-setup" event={props.event} show={show} onUpdate={handleShowUpdate} />}
        </TabContent>
      </div>}
    </div>
  );
});

export default ManageShow;